import {
  CatalogSort,
  FilterSpec,
  type BulkResult,
  type CatalogListItem,
  type Meta,
} from '@numistic/contracts'
import { useBulkCatalog, useCatalogList, useMeta, useSession } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Card, Checkbox, Input, Select, cn } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { z } from 'zod'

import { MutationErrors } from '../../components/mutation-errors.js'
import { useUrlSearchText } from '../../lib/url-search-text.js'
import { VisibilityBadge } from '../../catalog/shared.js'
import { GridSkeleton } from '../../components/grid-skeleton.js'
import { useErrorText } from '../../lib/errors.js'
import { BulkFieldEditor } from '../../meta/bulk-field.js'
import { FilterBuilder } from '../../meta/filter-builder.js'
import { MetaTable } from '../../meta/meta-table.js'
import { useRefResolver } from '../../meta/ref-resolver.js'
import { rootRoute } from '../root.js'

/**
 * Состояние списка в адресе. Роутер разбирает значения как JSON: «1961» в поиске приходит числом,
 * фильтр — объектом. Неверные значения не ломают страницу (catch).
 */
const Search = z.object({
  q: z.coerce.string().optional().catch(undefined),
  type: z.string().optional().catch(undefined),
  sort: CatalogSort.optional().catch(undefined),
  view: z.enum(['grid', 'table']).optional().catch(undefined),
  filter: FilterSpec.optional().catch(undefined),
  mine: z.boolean().optional().catch(undefined),
  owned: z.enum(['yes', 'no']).optional().catch(undefined),
})

export const catalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog',
  validateSearch: Search,
  component: CatalogPage,
})

function CatalogPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const search = catalogRoute.useSearch()
  const navigate = useNavigate({ from: '/catalog' })
  const meta = useMeta().data
  const { data: session } = useSession()
  const setQ = useCallback(
    (q: string | undefined) => void navigate({ search: (s) => ({ ...s, q }), replace: true }),
    [navigate],
  )
  const [text, setText] = useUrlSearchText(search.q, setQ)
  const [showFilters, setShowFilters] = useState(Boolean(search.filter))
  // Массовое редактирование — для редакторов (общие записи) и владельцев своих записей.
  const [selected, setSelected] = useState<Set<string> | null>(null)
  const editor = session?.user.role === 'admin' || session?.user.role === 'editor'
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const filter = useMemo<FilterSpec>(() => search.filter ?? { and: [] }, [search.filter])
  // Другая выборка — прежнее выделение к ней не относится.
  const queryKey = JSON.stringify([search.q, search.type, search.filter, search.mine, search.owned])
  const [selectionKey, setSelectionKey] = useState(queryKey)
  if (selectionKey !== queryKey) {
    setSelectionKey(queryKey)
    setSelected((prev) => (prev ? new Set() : prev))
  }

  const complete = filter.and.filter(
    (c) => c.value !== undefined || c.op === 'is_set' || c.op === 'is_not_set',
  )
  const list = useCatalogList({
    q: search.q,
    type: search.type,
    sort: search.sort,
    filter: complete.length ? JSON.stringify({ and: complete }) : undefined,
    mine: search.mine ? 'true' : undefined,
    owned: session ? search.owned : undefined,
    limit: 30,
  })
  const items = list.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold">{t('catalog.title')}</h1>
        {session ? (
          <>
            <Button asChild variant="outline">
              <Link to="/catalog/import/numista" search={{}}>
                {t('numista.open')}
              </Link>
            </Button>
            <Button asChild>
              <Link to="/catalog/new" search={{}}>
                {t('catalog.add')}
              </Link>
            </Button>
          </>
        ) : null}
      </div>

      <div className="grid gap-3">
        <Input
          type="search"
          aria-label={t('common.search')}
          placeholder={t('catalog.searchPlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="grid gap-3">
          <div
            role="group"
            aria-label={t('catalog.types')}
            className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
          >
            <TypeChip
              label={t('catalog.allTypes')}
              active={!search.type}
              onClick={() =>
                navigate({ search: (s) => ({ ...s, type: undefined, filter: undefined }) })
              }
            />
            {meta?.itemTypes
              .filter((x) => !x.code.startsWith('test_') && x.active !== false)
              .map((x) => (
                <TypeChip
                  key={x.code}
                  label={x.name.ru}
                  active={search.type === x.code}
                  onClick={() =>
                    navigate({ search: (s) => ({ ...s, type: x.code, filter: undefined }) })
                  }
                />
              ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            {session ? (
              <div
                role="group"
                aria-label={t('catalog.owned.label')}
                className="mr-auto inline-flex rounded-md border bg-card p-0.5 text-sm sm:mr-2"
              >
                {(['all', 'yes', 'no'] as const).map((o) => {
                  const active = (search.owned ?? 'all') === o
                  return (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        navigate({ search: (s) => ({ ...s, owned: o === 'all' ? undefined : o }) })
                      }
                      className={cn(
                        'rounded px-2.5 py-1 transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {t(`catalog.owned.${o}`)}
                    </button>
                  )
                })}
              </div>
            ) : null}
            {session ? (
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={Boolean(search.mine)}
                  onChange={(e) =>
                    navigate({ search: (s) => ({ ...s, mine: e.target.checked || undefined }) })
                  }
                />
                {t('catalog.mine')}
              </label>
            ) : null}
            <Select
              className="w-48"
              aria-label={t('catalog.sort')}
              value={search.sort ?? (search.q ? 'relevance' : 'title')}
              onChange={(e) =>
                navigate({ search: (s) => ({ ...s, sort: e.target.value as CatalogSort }) })
              }
            >
              {CatalogSort.options.map((o) => (
                <option key={o} value={o}>
                  {t(`catalog.sorts.${o}`)}
                </option>
              ))}
            </Select>
            <Select
              className="w-32"
              aria-label={t('catalog.viewTable')}
              value={search.view ?? 'grid'}
              onChange={(e) =>
                navigate({ search: (s) => ({ ...s, view: e.target.value as 'grid' | 'table' }) })
              }
            >
              <option value="grid">{t('catalog.viewGrid')}</option>
              <option value="table">{t('catalog.viewTable')}</option>
            </Select>
            {editor ? (
              <Button
                variant="outline"
                size="sm"
                aria-pressed={selected !== null}
                onClick={() => setSelected((v) => (v ? null : new Set()))}
              >
                {selected ? t('bulk.cancel') : t('bulk.select')}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              aria-expanded={showFilters}
              onClick={() => setShowFilters((v) => !v)}
            >
              {t('catalog.filters')}
              {complete.length ? ` (${complete.length})` : ''}
            </Button>
          </div>
        </div>
        {showFilters && meta ? (
          <Card className="p-4">
            <FilterBuilder
              meta={meta}
              typeCode={search.type}
              value={filter}
              onChange={(spec) =>
                navigate({
                  search: (s) => ({
                    ...s,
                    filter: spec.and.length ? spec : undefined,
                  }),
                  replace: true,
                })
              }
            />
          </Card>
        ) : null}
      </div>

      {selected && meta ? (
        <CatalogBulkBar
          meta={meta}
          ids={[...selected]}
          onSelectAll={() => setSelected(new Set(items.map((i) => i.id)))}
        />
      ) : null}
      {list.error ? <Alert variant="destructive">{errorText.request(list.error)}</Alert> : null}
      {list.isPending && meta ? (
        <GridSkeleton count={10} className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" />
      ) : null}
      {list.isSuccess && items.length === 0 ? (
        <p className="text-muted-foreground">{t('catalog.empty')}</p>
      ) : null}
      {meta && items.length ? (
        search.view === 'table' ? (
          <MetaTable
            meta={meta}
            rows={items.map((i) => ({ id: i.id, typeCode: i.typeCode, values: i.values }))}
            actions={(row) => (
              <>
                {selected ? (
                  <Checkbox
                    aria-label={t('bulk.selectItem', { title: String(row.values.title ?? '') })}
                    checked={selected.has(row.id)}
                    onChange={() => toggle(row.id)}
                  />
                ) : null}
                <OwnedBadge collection={items.find((i) => i.id === row.id)?.collection ?? null} />
                <Link
                  to="/catalog/$id"
                  params={{ id: row.id }}
                  className="text-sm text-primary underline underline-offset-4 hover:decoration-2"
                >
                  →
                </Link>
              </>
            )}
          />
        ) : (
          <CatalogGrid meta={meta} items={items} selected={selected} onToggle={toggle} />
        )
      ) : null}
      {list.hasNextPage ? (
        <Button
          variant="outline"
          className="justify-self-center"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {t('catalog.more')}
        </Button>
      ) : null}
    </div>
  )
}

function TypeChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  )
}

function CatalogGrid({
  meta,
  items,
  selected,
  onToggle,
}: {
  meta: Meta
  selected: Set<string> | null
  onToggle: (id: string) => void
  items: Array<{
    id: string
    typeCode: string
    title: string
    visibility: 'public' | 'members' | 'private'
    thumb: string | null
    values: Record<string, unknown>
    collection: CatalogListItem['collection']
  }>
}) {
  const { t } = useTranslation()
  const resolve = useRefResolver(meta, new Set(['issuers']))
  return (
    <ul
      className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
      aria-label={t('catalog.title')}
    >
      {items.map((item) => (
        <li key={item.id} className="relative">
          {selected ? (
            <Checkbox
              aria-label={t('bulk.selectItem', { title: item.title })}
              checked={selected.has(item.id)}
              onChange={() => onToggle(item.id)}
              className="absolute left-3 top-3 z-10 bg-card"
            />
          ) : null}
          <Link
            to="/catalog/$id"
            params={{ id: item.id }}
            className="group grid gap-2 rounded-lg border bg-card p-2 hover:border-primary/60"
          >
            <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-md bg-muted">
              <OwnedBadge collection={item.collection} className="absolute right-1.5 top-1.5" />
              {item.thumb ? (
                <img src={item.thumb} alt="" loading="lazy" className="size-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">{t('catalog.noImage')}</span>
              )}
            </div>
            <div className="grid gap-1 px-1 pb-1">
              <span className="line-clamp-2 text-sm font-medium group-hover:text-primary">
                {item.title}
              </span>
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span className="min-w-0 truncate">
                  {resolve('issuers', String(item.values.issuer ?? ''))}
                </span>
                <VisibilityBadge visibility={item.visibility} />
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

/** Метка на карточке: запись уже есть у меня (сколько штук) или стоит в «В поиске». */
function OwnedBadge({
  collection,
  className,
}: {
  collection: CatalogListItem['collection']
  className?: string
}) {
  const { t } = useTranslation()
  if (!collection || (!collection.owned && !collection.wanted)) return null
  const owned = collection.owned > 0
  return (
    <span
      className={cn(
        'z-[1] inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium shadow-sm',
        owned
          ? 'bg-primary text-primary-foreground'
          : 'border border-primary/50 bg-card text-foreground',
        className,
      )}
    >
      <span aria-hidden>{owned ? '✓' : '☆'}</span>
      {owned
        ? collection.owned > 1
          ? t('catalog.ownedBadgeCount', { count: collection.owned })
          : t('catalog.ownedBadge')
        : t('catalog.wantedBadge')}
    </span>
  )
}

function CatalogBulkBar({
  meta,
  ids,
  onSelectAll,
}: {
  meta: Meta
  ids: string[]
  onSelectAll: () => void
}) {
  const { t } = useTranslation()
  const bulk = useBulkCatalog()
  const [result, setResult] = useState<BulkResult | null>(null)
  const apply = (input: { visibility?: 'public' | 'members'; values?: Record<string, unknown> }) =>
    ids.length && bulk.mutate({ ids, ...input }, { onSuccess: setResult })
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-lg border bg-accent/40 p-2 text-sm"
      role="toolbar"
      aria-label={t('bulk.selected', { count: ids.length })}
    >
      <span>{t('bulk.selected', { count: ids.length })}</span>
      <Button variant="ghost" size="sm" onClick={onSelectAll}>
        {t('bulk.selectAll')}
      </Button>
      <Select
        className="w-56"
        aria-label={t('bulk.visibility')}
        value=""
        onChange={(e) =>
          e.target.value && apply({ visibility: e.target.value as 'public' | 'members' })
        }
      >
        <option value="">{t('bulk.visibility')}</option>
        {(['public', 'members'] as const).map((v) => (
          <option key={v} value={v}>
            {t(`catalog.visibilities.${v}`)}
          </option>
        ))}
      </Select>
      <BulkFieldEditor
        meta={meta}
        level="catalog"
        exclude={['title']}
        pending={bulk.isPending}
        onApply={(values) => apply({ values })}
      />
      {result ? (
        <span role="status" className="w-full text-xs text-muted-foreground">
          {t('bulk.result', { updated: result.updated })}
          {result.failed.length ? ` ${t('bulk.failed', { count: result.failed.length })}` : ''}
        </span>
      ) : null}
      <div className="w-full">
        <MutationErrors errors={[bulk.error]} />
      </div>
    </div>
  )
}
